/* eslint-disable no-magic-numbers */
import * as Adaptive from 'adaptivecards';

const ariaChecked = 'aria-checked';
export class RatingInput extends Adaptive.Input {
	static labelProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_6, 'label');
	static valueProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_6, 'value');

	constructor() {
		super();
		// Use the input's id property if available, else fallback to random id.
		this.baseId = this.id
			? `rating-${this.id}`
			: `rating-${Math.random().toString(36).slice(2, 9)}`;
	}

	get label() {
		return this.getValue(RatingInput.labelProperty);
	}

	set label(value) {
		if (this.label !== value) {
			this.setValue(RatingInput.labelProperty, value);
			this.updateLayout();
		}
	}

	get value() {
		return this.getValue(RatingInput.valueProperty);
	}

	set value(value) {
		if (this.value !== value) {
			this.setValue(RatingInput.valueProperty, value);
			this.updateLayout();
		}
	}

	isSet() {
		return this.value.length > 0;
	}

	internalRender() {
		this.labelId = `${this.baseId}-label`;
		// eslint-disable-next-line id-match
		this.ratingButtonIds = Array.from({ length: 5 }, (_, idx) => `${this.baseId}-star-${idx + 1}`);

		const labelBlock = new Adaptive.TextBlock();
		labelBlock.setParent(this);
		labelBlock.hostConfig = this.hostConfig;
		labelBlock.text = !this.label ? 'untitled' : this.label;
		labelBlock.wrap = this.wrap;
		this.labelElement = labelBlock.render();
		this.labelElement.id = this.labelId;

		const element = document.createElement('div');
		element.setAttribute('role', 'radiogroup');
		element.setAttribute('aria-labelledby', this.labelElement.id);
		this.ratingElements = [];
		for (let idx = 0; idx < 5; idx++) {
			this.ratingElements[idx] = document.createElement('button');
			this.ratingElements[idx].style.backgroundColor = 'transparent';
			this.ratingElements[idx].style.border = '0';
			this.ratingElements[idx].style.fontSize = '25px';

			this.ratingElements[idx].setAttribute('role', 'radio');
			this.ratingElements[idx].setAttribute(ariaChecked, 'false');
			this.ratingElements[idx].id = this.ratingButtonIds[idx];
			this.ratingElements[idx].onclick = () => {
				this.setValue(RatingInput.valueProperty, String(idx + 1));
				this.refreshRatingElements(idx + 1);
				this.valueChanged();
			};
			this.ratingElements[idx].onkeydown = (event) => {
				if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
					event.preventDefault();
					this.focusNextRating(idx);
				} else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
					event.preventDefault();
					this.focusPreviousRating(idx);
				}
			};
			element.append(this.ratingElements[idx]);
		}

		this.ratingElements[0].classList.add('ac-input-container');
		this.ratingElements[0].ariaRequired = 'true';
		this.labelElement.forElementId = this.ratingElements[0].id;
		return element;
	}

	focusNextRating(currentIndex) {
		const nextIndex = (currentIndex + 1) % this.ratingElements.length;
		this.ratingElements[nextIndex].focus();
	}

	focusPreviousRating(currentIndex) {
		const previousIndex =
			(currentIndex - 1 + this.ratingElements.length) % this.ratingElements.length;
		this.ratingElements[previousIndex].focus();
	}

	getJsonTypeName() {
		return 'Input.Rating';
	}

	parse(json, errors) {
		super.parse(json, errors);
		this.label = json.label;
		this.value = json.value;
	}

	refreshRatingElements() {
		for (let idx = 0; idx < this.ratingElements.length; idx++) {
			if (this.value > idx) {
				this.ratingElements[idx].innerText = '★';
				this.ratingElements[idx].style.color = 'rgb(250, 175, 0)';
				this.ratingElements[idx].setAttribute(ariaChecked, 'true');
				this.ratingElements[idx].setAttribute(
					'aria-label',
					`${idx + 1} star${idx > 0 ? 's' : ''} selected`,
				);
			} else {
				this.ratingElements[idx].innerText = '☆';
				this.ratingElements[idx].style.color = '#959393';
				this.ratingElements[idx].setAttribute(ariaChecked, 'false');
				this.ratingElements[idx].setAttribute('aria-label', `${idx + 1} star${idx > 0 ? 's' : ''}`);
			}
		}
	}

	updateLayout(processChildren = true) {
		super.updateLayout(processChildren);
		if (this.renderedElement) {
			this.refreshRatingElements(this.value);
		}
	}

	renderSpeech() {
		return `${this.value} stars`;
	}
}
